import { format } from 'date-fns';
import React, { useState } from 'react';
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  ArrowDropDown as ArrowDropDownIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  FileCopy as FileCopyIcon,
  FileCopyOutlined as FileCopyOutlinedIcon
} from '@material-ui/icons';

import api, { ApiParams } from '../../../common/api/api';
import { downloadFileUrl } from '../../../common/utils/downloadFileUrl';
import { FileExportType } from '../Document.interface';

export interface DocumentListExportButtonProps {
  folderName: string;
  folderId?: number;
  /** Selected file IDs. */
  fileIds?: number[];
  onDownload?: () => void;
}

const DocumentListExportButton: React.FC<
  DocumentListExportButtonProps
> = props => {
  const { folderName, folderId, fileIds, onDownload } = props;
  const theme = useTheme();
  const isBelowSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClose = () => setAnchorEl(null);

  const handleExport = async (
    options: {
      type?: FileExportType;
      includeAllVersions?: boolean;
    } = {}
  ) => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const params: ApiParams = { ...options, folderId, timeZone };
    if (fileIds) {
      params.fileIds = fileIds;
    }

    const nameExtras: string[] = [];
    if (fileIds && fileIds.length > 0) {
      nameExtras.push('Selected');
    }
    if (options.includeAllVersions) {
      nameExtras.push('All Versions');
    } else if (options.type === FileExportType.DocumentApproval) {
      nameExtras.push('Approval');
    }

    const date = format(new Date(), 'yyyy-MM-dd');
    const suffix = nameExtras.length > 0 ? ` (${nameExtras.join(', ')})` : '';
    const name = `${folderName}${suffix} - ${date}.zip`;
    const fileName = encodeURI(name);
    const url = new URL(`files/export/${fileName}`, api.getBaseUrl());
    const token = api.getAccessToken();
    token && url.searchParams.set('token', token);

    for (const [key, value] of Object.entries(params)) {
      const stringValue = Array.isArray(value)
        ? JSON.stringify(value)
        : value
          ? value.toString()
          : '';
      stringValue && url.searchParams.set(key, stringValue);
    }

    downloadFileUrl(url, name);
    onDownload?.();
    handleClose();
  };

  return (
    <>
      <Button
        size='small'
        color='primary'
        variant='contained'
        style={{ marginLeft: 'auto' }}
        endIcon={!isBelowSm && <ArrowDropDownIcon />}
        onClick={event => setAnchorEl(event.currentTarget)}
      >
        {isBelowSm ? <ArrowDropDownIcon /> : 'Export'}
      </Button>
      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          Export{' '}
          {fileIds && fileIds.length > 0
            ? 'Selected'
            : folderId
              ? 'this Folder'
              : 'All'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExport();
            handleClose();
          }}
        >
          <ListItemIcon>
            <FileCopyIcon />
          </ListItemIcon>
          <ListItemText primary='Latest Only' />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExport({ includeAllVersions: true });
            handleClose();
          }}
        >
          <ListItemIcon>
            <FileCopyOutlinedIcon />
          </ListItemIcon>
          <ListItemText primary='With all Versions' />
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExport({ type: FileExportType.DocumentApproval });
            handleClose();
          }}
        >
          <ListItemIcon>
            <AssignmentTurnedInIcon />
          </ListItemIcon>
          <ListItemText primary='Document Approval' />
        </MenuItem>
      </Menu>
    </>
  );
};

export default DocumentListExportButton;
